import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../components/common/Button'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { ConfirmModal } from '../components/common/Modal'
import { ContactForm } from '../components/common/ContactForm'
import { LanguageSettings } from '../components/profile/LanguageSettings'
import { useUserStore } from '../store/userStore'
import { signOutFirebase } from '../services/auth'
import {
  updateUserProfile,
  createUser,
  getElevenLabsVoices,
  createPortalSession,
  getSubscriptionStatus,
  getUserProfile,
  APIError,
  type ElevenLabsVoice,
  speakJapaneseText,
} from '../services/api'
import type { DifficultyLevel, PronunciationStrictness, ThemePreference } from '../../../shared/types'
import { AI_PERSONALITY_OPTIONS } from '../constants/aiPersonalities'

const DEFAULT_HIGH_CONFIDENCE_VOICE_IDS = ['pNInz6obpgDQGcFmaJgB', '21m00Tcm4TlvDq8ikWAM']
const RAW_ENV_JP_VOICE_IDS = (import.meta as any)?.env?.VITE_ELEVENLABS_JP_VOICE_IDS
const ENV_RECOMMENDED_VOICE_IDS =
  typeof RAW_ENV_JP_VOICE_IDS === 'string'
    ? RAW_ENV_JP_VOICE_IDS.split(',').map((id: string) => id.trim()).filter(Boolean)
    : []
const JAPANESE_SAMPLE_TEXT = 'こんにちは！この声で自然な日本語の練習をしてみましょう。'
const ENGLISH_SAMPLE_TEXT = 'Hello there! This is how the premium voice sounds in English.'

const computeJapaneseVoiceScore = (voice: ElevenLabsVoice): number => {
  let score = 0
  const normalizedName = voice.name.toLowerCase()
  const description = (voice.description || '').toLowerCase()
  const labelValues = Object.values(voice.labels || {})
    .join(' ')
    .toLowerCase()

  if (voice.supportsJapanese) score += 2
  if (DEFAULT_HIGH_CONFIDENCE_VOICE_IDS.includes(voice.voiceId)) score += 3
  if (normalizedName.startsWith('jp ')) score += 2
  if (normalizedName.includes('japanese')) score += 2
  if (description.includes('japanese')) score += 1
  if (labelValues.includes('japanese')) score += 2
  if (labelValues.includes('nihongo')) score += 1
  if (labelValues.includes('native')) score += 1
  if (labelValues.includes('studio') || labelValues.includes('premium') || labelValues.includes('pro')) score += 1
  if ((voice.labels?.accent || '').toLowerCase().includes('japanese')) score += 2
  if ((voice.labels?.language || '').toLowerCase().includes('japanese')) score += 2
  if (labelValues.includes('multilingual')) score += 1
  return score
}

const filterJapaneseOptimizedVoices = (voices: ElevenLabsVoice[]): ElevenLabsVoice[] => {
  const enriched = voices
    .map((voice) => ({
      voice,
      score: computeJapaneseVoiceScore(voice),
    }))
    .filter(({ voice }) => voice.supportsJapanese)

  const manualAllowList = new Set(ENV_RECOMMENDED_VOICE_IDS)
  if (manualAllowList.size > 0) {
    const allowListed = enriched.filter(({ voice }) => manualAllowList.has(voice.voiceId))
    if (allowListed.length > 0) {
      return allowListed
        .sort((a, b) => a.voice.name.localeCompare(b.voice.name))
        .map(({ voice }) => voice)
    }
  }

  const highConfidence = enriched.filter(({ score }) => score >= 4)
  if (highConfidence.length > 0) {
    return highConfidence
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score
        }
        return a.voice.name.localeCompare(b.voice.name)
      })
      .map(({ voice }) => voice)
  }

  return enriched
    .sort((a, b) => b.score - a.score)
    .slice(0, 8)
    .map(({ voice }) => voice)
}

export function Profile() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    user,
    setUser,
    updateUser,
    clearUser,
    setThemePreference: setAppTheme,
    aiPersonality,
    setAiPersonality,
  } = useUserStore()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [manageSubLoading, setManageSubLoading] = useState(false)

  // Form state
  const [username, setUsername] = useState('')
  const [difficultyLevel, setDifficultyLevel] = useState<DifficultyLevel>('beginner')
  const [dailyReviewTarget, setDailyReviewTarget] = useState(20)
  const [pronunciationStrictness, setPronunciationStrictness] =
    useState<PronunciationStrictness>('moderate')
  const [subscriptionTier, setSubscriptionTier] = useState<'free' | 'standard' | 'premium'>('free')
  const [preferredVoiceId, setPreferredVoiceId] = useState<string>('')
  const [personality, setPersonality] = useState<string>(() => aiPersonality || 'friendly_tutor')
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'light'
    const stored = localStorage.getItem('preferred-theme')
    return stored === 'dark' || stored === 'light' ? stored : 'light'
  })
  const [availableVoices, setAvailableVoices] = useState<ElevenLabsVoice[]>([])
  const [voicesLoading, setVoicesLoading] = useState(false)
  const [hasHandledCheckout, setHasHandledCheckout] = useState(false)
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null)
  const [previewVariant, setPreviewVariant] = useState<'jp' | 'en' | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)
  const selectedVoice = preferredVoiceId
    ? availableVoices.find((voice) => voice.voiceId === preferredVoiceId)
    : undefined

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setUsername(user.username)
      setDifficultyLevel(user.difficultyLevel)
      setDailyReviewTarget(user.dailyReviewTarget)
      setPronunciationStrictness(user.pronunciationStrictness as any)
      setSubscriptionTier(user.subscriptionTier || 'free')
      setPreferredVoiceId(user.preferredVoiceId || '')
      const nextTheme = (user.themePreference as ThemePreference) || 'light'
      setThemePreference(nextTheme)
      const currentTheme = useUserStore.getState().themePreference
      if (currentTheme !== nextTheme) {
        setAppTheme(nextTheme)
      }
    }
  }, [user, setAppTheme])

  useEffect(() => {
    if (preferredVoiceId) {
      setVoiceNotice(null)
      setPreviewError(null)
    }
  }, [preferredVoiceId])

  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause()
        previewAudioRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    setPersonality(aiPersonality || 'friendly_tutor')
  }, [aiPersonality])

  // Fetch available ElevenLabs voices when premium is selected
  useEffect(() => {
    const fetchVoices = async () => {
      if (subscriptionTier === 'premium' && availableVoices.length === 0) {
        setVoicesLoading(true)
        try {
          const response = await getElevenLabsVoices()
          const filteredVoices = filterJapaneseOptimizedVoices(response.voices || [])
          setAvailableVoices(filteredVoices)

          if (filteredVoices.length === 0) {
            setVoiceNotice(
              'We could not find any verified Japanese voices in your ElevenLabs project. Please add at least one Japanese-optimized voice and refresh.'
            )
          } else if (
            preferredVoiceId &&
            !filteredVoices.some((voice) => voice.voiceId === preferredVoiceId)
          ) {
            setVoiceNotice(
              'Your previous voice is no longer offered because it did not meet the Japanese pronunciation quality bar. Please pick one of the curated voices below.'
            )
            setPreferredVoiceId('')
          }
        } catch (error) {
          console.error('Failed to fetch voices:', error)
        } finally {
          setVoicesLoading(false)
        }
      }
    }
    fetchVoices()
  }, [subscriptionTier, availableVoices.length, preferredVoiceId])

  // Create user if none exists (first visit)
  useEffect(() => {
    const initUser = async () => {
      if (!user) {
        setIsLoading(true)
        try {
          // Create a default user
          const response = await createUser({
            username: `user_${Date.now()}`,
            email: undefined,
            difficultyLevel: 'beginner',
          })
          setUser(response.user)
        } catch (err) {
          console.error('Failed to create user:', err)
          setError('Failed to initialize user profile. Please try again.')
        } finally {
          setIsLoading(false)
        }
      }
    }

    initUser()
  }, [user, setUser])

  // Check for successful checkout and refresh subscription status
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user) {
        return
      }
      const checkoutSucceeded = searchParams.get('success') === 'true'
      if (!checkoutSucceeded || hasHandledCheckout) {
        return
      }

      setHasHandledCheckout(true)
      setSuccess(true)
      setError(null)

      try {
        console.log('Refreshing user data after successful checkout...')
        const updatedUser = await getUserProfile(user.id)
        console.log('Updated user data:', updatedUser.user)
        setUser(updatedUser.user)

        const { getAuth } = await import('firebase/auth')
        await getAuth().currentUser?.getIdToken(true)

        const subStatus = await getSubscriptionStatus(user.id)
        console.log('Subscription status from Stripe:', subStatus)
        setSubscriptionTier(subStatus.tier)
      } catch (err) {
        if (err instanceof APIError && err.statusCode === 401) {
          console.warn('Auth token not ready when fetching subscription status; will use stored tier instead.')
        } else {
          console.error('Failed to refresh subscription status:', err)
        }
      } finally {
        setSearchParams({}, { replace: true })
      }
    }

    checkSubscriptionStatus()
  }, [searchParams, user, setUser, hasHandledCheckout, setSearchParams])

  const playVoicePreviewSample = async (variant: 'jp' | 'en') => {
    if (!preferredVoiceId) {
      setPreviewError('Select a voice first to preview it.')
      return
    }

    setPreviewError(null)
    setPreviewVariant(variant)

    try {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause()
        previewAudioRef.current = null
      }

      const text = variant === 'jp' ? JAPANESE_SAMPLE_TEXT : ENGLISH_SAMPLE_TEXT
      const languageCode = variant === 'jp' ? 'ja-JP' : 'en-US'
      const preview = await speakJapaneseText(text, 'elevenlabs', preferredVoiceId, languageCode)

      if (!preview.audioUrl) {
        throw new Error('Preview audio is not available right now. Please try again in a few seconds.')
      }

      const audio = new Audio(preview.audioUrl)
      previewAudioRef.current = audio
      await audio.play()
    } catch (err) {
      console.error('Failed to play ElevenLabs preview sample:', err)
      setPreviewError(err instanceof Error ? err.message : 'Unable to play preview audio.')
    } finally {
      setPreviewVariant(null)
    }
  }

  const handleManageSubscription = async () => {
    if (!user) return

    setManageSubLoading(true)
    try {
      const { url } = await createPortalSession(window.location.href)
      window.location.href = url
    } catch (err) {
      console.error('Failed to open subscription portal:', err)
      const defaultMessage = 'Failed to open subscription management. Please try again.'
      if (err instanceof APIError) {
        if (err.code === 'NO_STRIPE_CUSTOMER') {
          setError(
            'We could not find an active Stripe subscription for this account. Please refresh or contact support if this persists.'
          )
        } else if (err.code === 'PORTAL_CREATION_FAILED') {
          setError('Stripe is temporarily unavailable. Please try opening the portal again in a moment.')
        } else {
          setError(err.message || defaultMessage)
        }
      } else {
        setError(defaultMessage)
      }
    } finally {
      setManageSubLoading(false)
    }
  }

  const handleThemeChoice = (theme: ThemePreference) => {
    setThemePreference(theme)
    setAppTheme(theme)
  }

  const handlePersonalityChoice = (id: string) => {
    setPersonality(id)
    setAiPersonality(id)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setIsLoading(true)
    setError(null)
    setSuccess(false)

    try {
      setAiPersonality(personality)
      const response = await updateUserProfile(user.id, {
        username,
        difficultyLevel,
        dailyReviewTarget,
        pronunciationStrictness,
        subscriptionTier,
        preferredVoiceId: preferredVoiceId || null,
        themePreference,
      })

      updateUser(response.user)
      setSuccess(true)

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    setShowLogoutModal(true)
  }

  const confirmLogout = () => {
    signOutFirebase().catch(() => {})
    clearUser()
    setShowLogoutModal(false)
    navigate('/auth')
  }

  if (!user && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-error-600 mb-4">{error || 'Failed to load profile'}</p>
          <Button onClick={() => window.location.reload()} variant="primary">
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      {/* Header */}
      <div className="bg-[var(--color-surface)] border-b border-[var(--color-border)] px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="text-primary-500 hover:text-primary-600 mb-4 flex items-center transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </button>
          <h1 className="text-3xl font-semibold text-[var(--color-text)]">User Profile</h1>
          <p className="text-[var(--color-muted)] mt-2">Manage your learning preferences and settings</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Preferences Form */}
        <div className="bg-[var(--color-surface)] rounded-xl shadow-md p-8">
          <h2 className="text-xl font-semibold text-[var(--color-text)] mb-6">Learning Preferences</h2>

          {error && (
            <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg text-error-700">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-success-50 border border-success-200 rounded-lg text-success-700">
              Profile updated successfully!
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                minLength={3}
                maxLength={30}
                pattern="[a-zA-Z0-9_]+"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter username"
              />
              <p className="mt-1 text-xs text-gray-500">
                3-30 characters, letters, numbers, and underscores only
              </p>
            </div>

            {/* Difficulty Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Difficulty Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['beginner', 'intermediate', 'advanced'] as DifficultyLevel[]).map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setDifficultyLevel(level)}
                    className={`px-4 py-3 border rounded-lg text-sm font-medium transition ${
                      difficultyLevel === level
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {difficultyLevel === 'beginner' && 'JLPT N5-N4 level, basic grammar and vocabulary'}
                {difficultyLevel === 'intermediate' && 'JLPT N3-N2 level, everyday conversations'}
                {difficultyLevel === 'advanced' && 'JLPT N1 level, complex topics and nuance'}
              </p>
            </div>

            {/* Pronunciation Strictness */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pronunciation Feedback Strictness
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['relaxed', 'moderate', 'strict'] as PronunciationStrictness[]).map(
                  (strictness) => (
                    <button
                      key={strictness}
                      type="button"
                      onClick={() => setPronunciationStrictness(strictness)}
                      className={`px-4 py-3 border rounded-lg text-sm font-medium transition ${
                        pronunciationStrictness === strictness
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {strictness.charAt(0).toUpperCase() + strictness.slice(1)}
                    </button>
                  )
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {pronunciationStrictness === 'relaxed' &&
                  'Get feedback only for significant pronunciation errors'}
                {pronunciationStrictness === 'moderate' && 'Balanced feedback for improvement'}
                {pronunciationStrictness === 'strict' &&
                  'Detailed feedback for near-native pronunciation'}
              </p>
            </div>

            {/* Daily Review Target */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Daily Review Target
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={dailyReviewTarget}
                  onChange={(e) => setDailyReviewTarget(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-2xl font-bold text-primary-600 w-16 text-center">
                  {dailyReviewTarget}
                </span>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Number of flashcards to review each day
              </p>
            </div>

            {/* Theme Preference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Appearance</label>
              <div className="grid grid-cols-2 gap-3">
                {(['light', 'dark'] as ThemePreference[]).map((theme) => (
                  <button
                    key={theme}
                    type="button"
                    onClick={() => handleThemeChoice(theme)}
                    className={`px-4 py-3 border rounded-lg text-sm font-medium transition ${
                      themePreference === theme
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {theme === 'light' ? 'Light mode' : 'Dark mode'}
                  </button>
                ))}
              </div>
            </div>

            {/* AI Personality */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">AI Personality</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AI_PERSONALITY_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handlePersonalityChoice(option.id)}
                    className={`w-full text-left border rounded-lg p-4 transition ${
                      personality === option.id
                        ? 'border-primary-500 bg-primary-50 shadow-sm'
                        : 'border-gray-300 bg-white hover:border-gray-400'
                    }`}
                    aria-pressed={personality === option.id}
                  >
                    <div className="font-semibold text-gray-900">{option.title}</div>
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Subscription Tier */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Tier
              </label>
              <div className="grid grid-cols-3 gap-3">
                <div
                  className={`p-4 border-2 rounded-lg ${
                    subscriptionTier === 'free'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-semibold text-gray-900">Free</span>
                    {subscriptionTier === 'free' && (
                      <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Google TTS</p>
                  <p className="text-sm text-gray-600">Limited AI</p>
                </div>

                <div
                  className={`p-4 border-2 rounded-lg ${
                    subscriptionTier === 'standard'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-semibold text-gray-900">Standard</span>
                    {subscriptionTier === 'standard' && (
                      <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Google TTS</p>
                  <p className="text-sm text-gray-600">Gemini AI</p>
                </div>

                <div
                  className={`p-4 border-2 rounded-lg ${
                    subscriptionTier === 'premium'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-semibold text-gray-900">Premium</span>
                    {subscriptionTier === 'premium' && (
                      <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">ElevenLabs AI</p>
                  <p className="text-sm text-gray-600">Claude AI</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  {subscriptionTier === 'free'
                    ? 'Want premium AI voices and unlimited practice?'
                    : 'Manage your subscription and billing'}
                </p>
                <button
                  type="button"
                  onClick={() => subscriptionTier === 'free' ? navigate('/pricing') : handleManageSubscription()}
                  disabled={manageSubLoading}
                  className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                >
                  {manageSubLoading ? 'Loading...' : subscriptionTier === 'free' ? 'Upgrade' : 'Manage'}
                </button>
              </div>
            </div>

            {/* Voice Selection (Premium only) */}
            {subscriptionTier === 'premium' && (
              <div>
                <label htmlFor="voice" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Voice
                </label>
                {voicesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-600">Loading voices...</span>
                  </div>
                ) : availableVoices.length > 0 ? (
                  <>
                    <select
                      id="voice"
                      value={preferredVoiceId}
                      onChange={(e) => setPreferredVoiceId(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Default voice</option>
                      {availableVoices.map((voice) => (
                        <option key={voice.voiceId} value={voice.voiceId}>
                          {voice.supportsJapanese ? '🇯🇵 ' : ''}
                          {voice.name}
                          {voice.labels?.gender ? ` (${voice.labels.gender})` : ''}
                          {voice.labels?.accent ? ` - ${voice.labels.accent}` : ''}
                          {voice.supportsJapanese ? ' - Multilingual' : ''}
                        </option>
                      ))}
                    </select>
                    {voiceNotice && (
                      <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {voiceNotice}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      🇯🇵 = Recommended for Japanese (Multilingual voices)
                    </p>
                    {selectedVoice && (
                      <div className="mt-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {selectedVoice.previewUrl && (
                            <button
                              type="button"
                              onClick={() => {
                                if (selectedVoice.previewUrl) {
                                  const audio = new Audio(selectedVoice.previewUrl)
                                  audio.play()
                                }
                              }}
                              className="text-xs px-3 py-1 bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200 transition"
                            >
                              Default preview
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => playVoicePreviewSample('jp')}
                            disabled={previewVariant !== null}
                            className="text-xs px-3 py-1 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition"
                          >
                            {previewVariant === 'jp' ? 'Playing...' : 'Japanese phrase'}
                          </button>
                          <button
                            type="button"
                            onClick={() => playVoicePreviewSample('en')}
                            disabled={previewVariant !== null}
                            className="text-xs px-3 py-1 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition"
                          >
                            {previewVariant === 'en' ? 'Playing...' : 'English phrase'}
                          </button>
                        </div>
                        {previewError && <p className="text-xs text-red-600">{previewError}</p>}
                        <span className="text-xs text-gray-500">
                          {selectedVoice.description || 'Premium multilingual model tuned for Japanese coaching.'}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500 py-2">
                    No voices available. Please check your ElevenLabs configuration.
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Only Japanese-vetted ElevenLabs voices are shown to keep pronunciation quality high.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/')}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>

        {/* Language Settings */}
        <div className="mt-8 bg-[var(--color-surface)] rounded-xl shadow-md p-8">
          <h2 className="text-xl font-semibold text-[var(--color-text)] mb-6">Language Settings</h2>
          <LanguageSettings />
        </div>

        {/* Account Info */}
        <div className="mt-8 bg-[var(--color-surface)] rounded-xl shadow-md p-8">
          <h2 className="text-xl font-semibold text-[var(--color-text)] mb-4">Account Information</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Username:</span>
              <span className="font-medium text-gray-900">{user.username}</span>
            </div>
            {user.email && (
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium text-gray-900">{user.email}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Member since:</span>
              <span className="font-medium text-gray-900">
                {new Date(user.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last activity:</span>
              <span className="font-medium text-gray-900">
                {new Date(user.lastActivityDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Contact & Feedback Section */}
        <ContactForm className="mt-8" />

        {/* Account Actions */}
        <div className="mt-6 bg-[var(--color-surface)] rounded-xl shadow-md p-8">
          <h2 className="text-xl font-semibold text-[var(--color-text)] mb-4">Account Actions</h2>
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleLogout}>Log Out</Button>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={confirmLogout}
        title="Log Out"
        message="Are you sure you want to log out?"
        confirmText="Log Out"
        cancelText="Cancel"
        variant="primary"
      />
    </div>
  )
}

export default Profile

